import { useMemo, type CSSProperties } from 'react';

import {
    autoUpdate,
    flip,
    hide,
    offset,
    shift,
    size,
    useFloating,
    useFloatingNodeId
} from '@floating-ui/react';
import { flushSync } from 'react-dom';

import type { SelectRootProps } from '../../Select.types';

type UseSelectFloatingProps = {
    open: boolean | undefined;
    canDropdownOpen: boolean;
    setOpen: (open: boolean) => void;
    onDropdownVisibleChange: ((visible: boolean) => void) | undefined;
    listHeight: number;
    setMaxHeight: (height: number) => void;
} & Pick<SelectRootProps, 'dropdownMatchSelectWidth'>;

const OVERFLOW_PADDING = 4;

export const useSelectFloating = ({
    open,
    canDropdownOpen,
    setOpen,
    onDropdownVisibleChange,
    listHeight,
    setMaxHeight,
    dropdownMatchSelectWidth
}: UseSelectFloatingProps) => {
    const nodeId = useFloatingNodeId();
    const { refs, floatingStyles, context, isPositioned } = useFloating<HTMLInputElement>({
        open: open && canDropdownOpen,
        nodeId,
        onOpenChange: (newOpen) => {
            flushSync(() => {
                setOpen(newOpen);
                onDropdownVisibleChange?.(newOpen);
            });
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(8),
            flip({ padding: OVERFLOW_PADDING }),
            shift(),
            size({
                apply({ availableHeight, rects, elements }) {
                    // Use state instead of directly mutating so that `react-virtual`
                    // scrollToIndex() function works in the layout effect.
                    flushSync(() => {
                        setMaxHeight(Math.min(availableHeight, listHeight));
                    });

                    // Dropdown width logic
                    if (typeof dropdownMatchSelectWidth === 'number') {
                        // If dropdownMatchSelectWidth is a number, use it as the width.
                        // Note: the dropdown size never can be smaller than the select size
                        const dropdownWidth = Math.max(
                            dropdownMatchSelectWidth,
                            rects.reference.width
                        );
                        Object.assign(elements.floating.style, {
                            width: `${dropdownWidth}px`
                        });
                    } else if (dropdownMatchSelectWidth === false) {
                        // Only set minWidth when explicitly set to false
                        Object.assign(elements.floating.style, {
                            minWidth: `${rects.reference.width}px`
                        });
                    } else {
                        // Default behavior: match width exactly (dropdownMatchSelectWidth is true or undefined)
                        Object.assign(elements.floating.style, {
                            width: `${rects.reference.width}px`
                        });
                    }
                },
                padding: OVERFLOW_PADDING
            }),
            hide({ strategy: 'referenceHidden' })
        ],
        placement: 'bottom-start'
    });

    const computedFloatingStyles = useMemo(
        () => ({
            ...floatingStyles,
            ...((context.middlewareData.hide?.referenceHidden && {
                visibility: 'hidden',
                pointerEvents: 'none'
            }) as CSSProperties)
        }),
        [floatingStyles, context.middlewareData.hide?.referenceHidden]
    );

    const contextProps = useMemo(
        () => ({
            refs,
            context,
            isPositioned,
            computedFloatingStyles,
            nodeId
        }),
        [refs, context, isPositioned, computedFloatingStyles, nodeId]
    );

    return contextProps;
};

export type SelectFloatingReturn = ReturnType<typeof useSelectFloating>;
